import config from "../config";
import { getAuthToken } from "./userIdentity";
import { getCountryByCode, resolveCountryName } from "./countries";

const SEARCH_MIN_LENGTH = 4; // greater than 3
const SEARCH_DEBOUNCE_MS = 550;

/** Random opaque session token for a Places autocomplete → details pairing. */
export function createPlacesSessionToken() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  const bytes =
    typeof crypto !== "undefined" && crypto.getRandomValues
      ? crypto.getRandomValues(new Uint8Array(16))
      : Array.from({ length: 16 }, () => Math.floor(Math.random() * 256));
  return Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
}

function authHeaders() {
  const token = getAuthToken();
  const headers = { "Content-Type": "application/json" };
  if (token) headers.Authorization = `Token ${token}`;
  return headers;
}

function isApiFailure(response, json) {
  if (!response.ok) return true;
  const type = String(json?.message?.type || "").toLowerCase();
  // Backend uses "Success" / "success" / "error"
  return type === "error" || type === "fail" || type === "failed";
}

function looksLikeGooglePlace(obj) {
  if (!obj || typeof obj !== "object") return false;
  return Boolean(
    (Array.isArray(obj.addressComponents) && obj.addressComponents.length) ||
      (Array.isArray(obj.address_components) && obj.address_components.length) ||
      obj.formattedAddress ||
      obj.formatted_address
  );
}

function looksLikeFlatPlace(obj) {
  if (!obj || typeof obj !== "object") return false;
  return Boolean(
    obj.address ||
      obj.city ||
      obj.postcode ||
      obj.post_code ||
      obj.place_id
  );
}

function unwrapData(payload) {
  if (!payload || typeof payload !== "object") return payload;
  const candidates = [
    payload.data,
    payload.result,
    payload.place,
    payload.data?.data,
    payload.data?.result,
    payload.data?.place,
    payload,
  ];
  for (const candidate of candidates) {
    if (!candidate || typeof candidate !== "object") continue;
    if (Array.isArray(candidate.suggestions)) return candidate;
    if (looksLikeGooglePlace(candidate)) return candidate;
    if (looksLikeFlatPlace(candidate)) return candidate;
  }
  if (payload.data != null && typeof payload.data === "object") {
    return payload.data;
  }
  return payload;
}

export function mapAutocompleteSuggestions(payload) {
  const root = unwrapData(payload) || {};
  // Prefer explicit data.suggestions when present (production Success envelope).
  const suggestions = Array.isArray(root.suggestions)
    ? root.suggestions
    : Array.isArray(payload?.data?.suggestions)
      ? payload.data.suggestions
      : [];

  return suggestions
    .map((entry, index) => {
      const prediction = entry?.placePrediction || entry || {};
      const placeId = String(
        prediction.placeId || prediction.place_id || ""
      ).trim();
      // Google sometimes returns "places/ChIJ..." — strip prefix for details API.
      const normalizedPlaceId = placeId.replace(/^places\//i, "");
      const label =
        prediction.text?.text ||
        [
          prediction.structuredFormat?.mainText?.text,
          prediction.structuredFormat?.secondaryText?.text,
        ]
          .filter(Boolean)
          .join(", ") ||
        "";
      if (!normalizedPlaceId || !label) return null;
      return {
        id: normalizedPlaceId,
        placeId: normalizedPlaceId,
        label,
        mainText: prediction.structuredFormat?.mainText?.text || label,
        secondaryText: prediction.structuredFormat?.secondaryText?.text || "",
        key: `${normalizedPlaceId}-${index}`,
      };
    })
    .filter(Boolean);
}

function componentByType(components, type) {
  const list = Array.isArray(components) ? components : [];
  return (
    list.find((c) => Array.isArray(c.types) && c.types.includes(type)) || null
  );
}

function componentText(component) {
  if (!component) return "";
  return String(
    component.longText ||
      component.long_name ||
      component.shortText ||
      component.short_name ||
      ""
  ).trim();
}

function componentShort(component) {
  if (!component) return "";
  return String(
    component.shortText ||
      component.short_name ||
      component.longText ||
      component.long_name ||
      ""
  ).trim();
}

function firstComponentText(components, types) {
  for (const type of types) {
    const text = componentText(componentByType(components, type));
    if (text) return text;
  }
  return "";
}

function extractPostcodeFromText(text) {
  const raw = String(text || "");
  if (!raw) return "";
  const uk = raw.match(/\b([A-Z]{1,2}\d[A-Z\d]?\s*\d[A-Z]{2})\b/i);
  if (uk) return uk[1].toUpperCase().replace(/\s+/, " ");
  const ie = raw.match(/\b([A-Z]\d{2}\s*[A-Z0-9]{4})\b/i);
  if (ie) return ie[1].toUpperCase().replace(/\s+/, " ");
  return "";
}

function normalizeLocation(root) {
  if (root?.latitude != null && root?.longitude != null) {
    return { lat: root.latitude, lng: root.longitude };
  }
  const loc = root?.location;
  if (!loc || typeof loc !== "object") return null;
  if (loc.latitude != null && loc.longitude != null) {
    return { lat: loc.latitude, lng: loc.longitude };
  }
  if (loc.lat != null && loc.lng != null) {
    return { lat: loc.lat, lng: loc.lng };
  }
  return loc;
}

function mapFromGoogleComponents(root) {
  const components = root.addressComponents || root.address_components || [];
  const formatted = String(
    root.formattedAddress || root.formatted_address || ""
  ).trim();

  const city =
    firstComponentText(components, [
      "postal_town",
      "locality",
      "sublocality_level_1",
      "sublocality",
      "administrative_area_level_2",
      "administrative_area_level_1",
    ]) || "";

  const countryComp = componentByType(components, "country");
  const countryLong = componentText(countryComp);
  const countryShort = componentShort(countryComp).toUpperCase();
  const countryName = resolveCountryName(
    (countryShort && getCountryByCode(countryShort)?.name) || countryLong || ""
  );

  let postCode =
    firstComponentText(components, ["postal_code", "postal_code_prefix"]) ||
    extractPostcodeFromText(formatted);

  // Prefer full formatted address for both Address and Line 1 fields.
  const address = formatted;
  const streetNumber = firstComponentText(components, ["street_number"]);
  const route = firstComponentText(components, ["route"]);
  const premise = firstComponentText(components, ["premise", "subpremise"]);
  const streetLine = [streetNumber, route].filter(Boolean).join(" ").trim();
  const line2 = premise && streetLine ? premise : "";

  return {
    line1: address || streetLine || premise || "",
    line2,
    line3: city,
    address: address || streetLine || city,
    country: countryName,
    countryCode: countryShort || "",
    postCode: postCode ? String(postCode).toUpperCase() : "",
    location: normalizeLocation(root),
    placeId: String(root.place_id || root.placeId || "").replace(
      /^places\//i,
      ""
    ),
  };
}

function mapFromFlatApi(root) {
  const address = String(root.address || "").trim();
  const city = String(root.city || "").trim();
  const countryName = resolveCountryName(String(root.country || "").trim());
  const postCode = String(
    root.postcode || root.post_code || root.postalCode || root.postal_code || ""
  ).trim();

  return {
    line1: address || city || "",
    line2: "",
    line3: city,
    address: address || city,
    country: countryName,
    countryCode: "",
    postCode: postCode ? postCode.toUpperCase() : "",
    location: normalizeLocation(root),
    placeId: String(root.place_id || root.placeId || "").replace(
      /^places\//i,
      ""
    ),
  };
}

/**
 * Map Places details payload into SearchCity form fields.
 * Supports:
 * 1) Production Google Places shape: formattedAddress + addressComponents
 * 2) Normalized flat shape: address, city, country, postcode
 */
export function mapPlaceDetailsToForm(payload) {
  const root = unwrapData(payload) || {};

  if (looksLikeGooglePlace(root)) {
    return mapFromGoogleComponents(root);
  }

  if (looksLikeFlatPlace(root)) {
    return mapFromFlatApi(root);
  }

  // Last resort: try Google mapper anyway (may still extract location/address).
  return mapFromGoogleComponents(root);
}

export async function fetchPlacesAutocomplete({
  input,
  sessionToken,
  signal,
}) {
  const response = await fetch(
    `${config.baseUrl}api/v1/places_autocomplete/`,
    {
      method: "POST",
      headers: authHeaders(),
      body: JSON.stringify({
        input,
        session_token: sessionToken,
      }),
      signal,
    }
  );
  let json = null;
  try {
    json = await response.json();
  } catch (e) {
    json = null;
  }
  if (isApiFailure(response, json)) {
    const err = new Error(
      json?.message?.description || "Address search failed."
    );
    err.payload = json;
    throw err;
  }
  return mapAutocompleteSuggestions(json);
}

export async function fetchPlacesDetails({ placeId, sessionToken, signal }) {
  const normalizedPlaceId = String(placeId || "")
    .trim()
    .replace(/^places\//i, "");
  const response = await fetch(`${config.baseUrl}api/v1/places_details/`, {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify({
      place_id: normalizedPlaceId,
      session_token: sessionToken,
    }),
    signal,
  });
  let json = null;
  try {
    json = await response.json();
  } catch (e) {
    json = null;
  }
  if (isApiFailure(response, json)) {
    const err = new Error(
      json?.message?.description || "Could not load address details."
    );
    err.payload = json;
    throw err;
  }
  return mapPlaceDetailsToForm(json);
}

export const placesSearchConfig = {
  minLength: SEARCH_MIN_LENGTH,
  debounceMs: SEARCH_DEBOUNCE_MS,
};
